<style scoped>
/* Note-level review actions: review-status select + comment/clarify icon
   buttons (draft-era Correct/Compare icons removed — corrections are
   per-record on extraction cards, peer comparison has no Phase 4 endpoint). */
.review-toolbar {
	display: flex;
	gap: 0.25rem;
	flex-shrink: 0;
	align-items: center;
}

.toolbar-status {
	width: auto;
	min-width: 11.5rem;
	font-size: 0.8125rem;
}
</style>

<template>
	<div class="review-toolbar" role="toolbar" aria-label="Review actions">
		<select
			v-model="draftStatus"
			class="form-select form-select-sm toolbar-status"
			:disabled="busy"
			title="Review status (PUT /api/transcript/{id}/status)"
			aria-label="Review status"
			@change="onStatusChange"
		>
			<option v-for="candidate in statuses" :key="candidate" :value="candidate">
				{{ candidate }}
			</option>
		</select>
		<button
			class="btn btn-sm btn-outline-secondary"
			type="button"
			title="Comment"
			:disabled="busy"
			@click="askComment"
		>
			<i class="bi bi-chat-left-text"></i>
		</button>
		<button
			class="btn btn-sm btn-outline-secondary"
			type="button"
			title="Clarify"
			:disabled="busy"
			@click="askClarify"
		>
			<i class="bi bi-question-circle"></i>
		</button>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import { TranscriptReviewStatus } from "@/type/api";

interface Props {
	status: TranscriptReviewStatus;
	busy?: boolean;
}
interface Emits {
	comment: [findings: string];
	clarify: [question: string];
	"status-change": [status: TranscriptReviewStatus];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const statuses: TranscriptReviewStatus[] = [
	"Recorded",
	"In Review",
	"Awaiting Clarification",
	"Superseded",
	"Closed",
];

// Draft mirrors the note's status; it tracks the prop so a failed PUT (the
// parent re-renders with the unchanged status) snaps the select back.
const draftStatus = ref<TranscriptReviewStatus>(props.status);
watch(
	() => props.status,
	(status) => {
		draftStatus.value = status;
	},
);

const onStatusChange = () => {
	if (draftStatus.value !== props.status)
		emit("status-change", draftStatus.value);
};

const askComment = () => {
	const findings = window.prompt("Comment / finding on this transcript:", "");
	if (findings?.trim()) emit("comment", findings.trim());
};

const askClarify = () => {
	const question = window.prompt("Question for the technician:", "");
	if (question?.trim()) emit("clarify", question.trim());
};
</script>
